import { useCallback, useState } from "react";
import {
  StoredPaymentRetries,
  clearStoredPaymentRetries,
  getStoredPaymentRetries,
  setStoredPaymentRetries,
} from "../utils/storage";

export enum PaymentInitiationUrlParams {
  PROVIDER = "provider",
  USER_ID = "userId",
  PLAN_ID = "planId",
  COUNTRY = "countryCode",
  CURRENCY = "currency",
  SPECIAL_OFFER = "isSpecialOffer",
  DATA = "data",
  PAYMENT_METHOD = "method",
}

const INITIAL_PAYMENT_RETRY_COUNT = 0;
export const MAX_PAYMENT_RETRIES = 3;

export function generatePaymentParams(queryParams: URLSearchParams) {
  const provider = queryParams.get(PaymentInitiationUrlParams.PROVIDER);
  const userId = queryParams.get(PaymentInitiationUrlParams.USER_ID);
  const planId = queryParams.get(PaymentInitiationUrlParams.PLAN_ID);
  const countryCode = queryParams.get(PaymentInitiationUrlParams.COUNTRY);
  const currency =
    queryParams.get(PaymentInitiationUrlParams.CURRENCY) || "NGN";
  const isSpecialOffer = queryParams.get(
    PaymentInitiationUrlParams.SPECIAL_OFFER
  );

  return {
    provider,
    userId,
    planId,
    countryCode,
    currency,
    isSpecialOffer: isSpecialOffer === "true",
  };
}

export default function usePayments() {
  const [paymentRetries, setPaymentRetries] = useState<StoredPaymentRetries>(
    () => getStoredPaymentRetries() ?? {}
  );

  const updatePaymentRetries = useCallback(
    (updatedPaymentRetries: StoredPaymentRetries) => {
      setPaymentRetries(updatedPaymentRetries);
      setStoredPaymentRetries(updatedPaymentRetries);
    },
    []
  );

  const generatePaymentRetryData = useCallback(
    (retryCount = INITIAL_PAYMENT_RETRY_COUNT) => {
      return { retryCount, lastUpdatedAt: Date.now() };
    },
    []
  );

  const deletePaymentRetryData = useCallback(
    (planId: string) => {
      const { [planId]: currentPlanRetry, ...otherPlansRetries } =
        paymentRetries;

      if (Object.keys(otherPlansRetries).length === 0) {
        clearStoredPaymentRetries();
        return;
      }
      updatePaymentRetries(otherPlansRetries);
    },
    [paymentRetries, updatePaymentRetries]
  );

  const getPaymentRetryCount = useCallback(
    (planId: string) => {
      const paymentRetryForPlan =
        paymentRetries[planId] ?? generatePaymentRetryData();
      const timeSinceLastRetry =
        (Date.now() - paymentRetryForPlan.lastUpdatedAt) / (60 * 1000); // time in minutes
      const paymentRetryCount =
        timeSinceLastRetry > 10
          ? INITIAL_PAYMENT_RETRY_COUNT
          : paymentRetryForPlan.retryCount;

      return paymentRetryCount;
    },
    [paymentRetries, generatePaymentRetryData]
  );

  return {
    paymentRetries,
    updatePaymentRetries,
    generatePaymentRetryData,
    deletePaymentRetryData,
    getPaymentRetryCount,
  };
}
